import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useBrandMode } from "./BrandModeContext";
import { fetchComposeOffer, submitOffer } from "./services/brandModeServices";
import { fmtMoney, fmtRange, fullName } from "./brandUtils";
import {
  CONTENT_TYPE_OPTIONS,
  PLATFORM_OPTIONS,
  brandCost,
  buildMilestones,
  deliverableLines,
  estimateFromRates,
  fmtDateRange,
  fmtLongRange,
  rebalanceMilestones,
  timeAgo,
} from "./brandDealUtils";
import {
  ActionBar,
  BackLink,
  Badge,
  Btn,
  CampaignContextBar,
  Card,
  Checkbox,
  ErrorState,
  Input,
  Loading,
  Overline,
  Select,
  TextArea,
  Toggle,
} from "./ui/BrandUI";

const Section = ({ n, title, right, children }) => (
  <Card className="flex flex-col gap-4">
    <div className="flex items-center justify-between">
      <Overline>
        {n}. {title}
      </Overline>
      {right}
    </div>
    {children}
  </Card>
);

const GrayBox = ({ children, className = "" }) => (
  <div className={`rounded-[8px] border border-bm-strong bg-bm-subtle p-4 bm-body-m text-bm-ink ${className}`}>{children}</div>
);

const ComposeOfferPage = () => {
  const navigate = useNavigate();
  const { campaignId, athleteId } = useParams();
  const [params] = useSearchParams();
  const { refreshBadges, refreshCampaigns } = useBrandMode();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null); // 'send' | 'draft'

  // Editable offer state
  const [compensation, setCompensation] = useState("");
  const [rows, setRows] = useState([]);
  const [editingRows, setEditingRows] = useState(false);
  const [milestones, setMilestones] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [exclusivityEnabled, setExclusivityEnabled] = useState(true);
  const [exclusivityDays, setExclusivityDays] = useState(14);
  const [requireProof, setRequireProof] = useState(true);
  const [scopeOfWork, setScopeOfWork] = useState("");
  const [message, setMessage] = useState("");
  const [extraIds, setExtraIds] = useState(() =>
    (params.get("athletes") || "").split(",").map((s) => s.trim()).filter((s) => s && s !== athleteId),
  );
  const [showOthers, setShowOthers] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const payload = await fetchComposeOffer(campaignId, athleteId);
        if (cancelled) return;
        setData(payload);
        const o = payload.offer || {};
        setCompensation(o.compensation ? Number(o.compensation).toLocaleString() : "");
        setRows(o.deliverables || []);
        setMilestones(o.milestones || []);
        setStartDate(o.startDate || "");
        setEndDate(o.endDate || "");
        setExclusivityEnabled(o.exclusivityEnabled ?? true);
        setExclusivityDays(o.exclusivityDays ?? 14);
        setRequireProof(o.requireProof ?? true);
        setScopeOfWork(o.scopeOfWork || "");
        setMessage(o.message || "");
        setShowOthers(extraIds.length > 0);
      } catch (err) {
        if (!cancelled) setError(err.message || "Could not load the offer");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, athleteId]);

  const comp = Number(String(compensation).replace(/,/g, "")) || 0;
  const cost = brandCost(comp);
  const structure = data?.campaign?.budget?.paymentStructure || "milestone";
  const lines = useMemo(() => deliverableLines(rows, data?.campaign?.adUsageDays), [rows, data]);
  const estimate = useMemo(() => estimateFromRates(data?.athlete?.nilRates, rows), [data, rows]);

  // Keep the schedule in step with compensation edits unless the user has
  // customised amounts (then just rebalance the final tranche).
  useEffect(() => {
    if (!data) return;
    setMilestones((prev) => (prev.length ? rebalanceMilestones(prev, comp) : buildMilestones(structure, comp, rows)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comp]);

  const setRow = (i, patch) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  const submit = async (action) => {
    if (action === "send" && comp <= 0) return toast.error("Enter athlete compensation before sending.");
    setSaving(action);
    try {
      const payload = {
        action,
        athleteIds: [athleteId, ...extraIds],
        compensation: comp,
        deliverables: rows,
        milestones,
        startDate,
        endDate,
        exclusivityEnabled,
        exclusivityDays,
        requireProof,
        scopeOfWork,
        message,
      };
      const result = await submitOffer(campaignId, athleteId, payload);
      const n = result.deals?.length || 1;
      toast.success(action === "send" ? `Offer sent to ${n} athlete${n === 1 ? "" : "s"}.` : "Offer draft saved.");
      refreshBadges();
      refreshCampaigns();
      navigate(`/brand/campaigns/${campaignId}`);
    } catch (err) {
      toast.error(err.message || "Could not save the offer");
    } finally {
      setSaving(null);
    }
    return null;
  };

  if (error) return <div className="py-10"><ErrorState>{error}</ErrorState></div>;
  if (!data) return <Loading label="Preparing the offer..." />;

  const { campaign, athlete, deal, brand, otherUncontacted = [], offerOpenDays } = data;
  const alreadySent = deal && deal.status !== "draft";
  const targets = 1 + extraIds.length;

  return (
    <div className="flex flex-col gap-5 pt-6">
      <BackLink to={`/brand/campaigns/${campaignId}`}>Back to campaign</BackLink>
      <CampaignContextBar
        campaign={campaign}
        linkTo={`/brand/campaigns/${campaignId}`}
        right={
          <span>
            {[fmtDateRange(campaign.startDate, campaign.endDate), campaign.budget?.total ? `${fmtMoney(campaign.budget.total)} budget` : null, `${campaign.athleteIds?.length || 0} athletes`]
              .filter(Boolean)
              .join("  ·  ")}
          </span>
        }
      />

      <div className="flex flex-col gap-[6px]">
        <Overline brand>Deal flow</Overline>
        <h1 className="bm-h-l text-bm-ink">Compose Offer</h1>
        <p className="bm-body-l text-bm-muted">Offers are sent in-app with email notification.</p>
      </div>

      {alreadySent && (
        <Card className="!bg-bm-subtle flex flex-wrap items-center justify-between gap-3">
          <span className="bm-body-m text-bm-muted">
            An offer went out {timeAgo(deal.offer?.sentAt)} and is {deal.statusLabel.toLowerCase()}
            {deal.offerDaysLeft !== null && deal.offerDaysLeft !== undefined ? ` · expires in ${deal.offerDaysLeft} day${deal.offerDaysLeft === 1 ? "" : "s"}` : ""}.
            Sending again issues a revised version.
          </span>
          <Badge tone="outline">Version {deal.offer?.version || 1}</Badge>
        </Card>
      )}

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-5">
          {/* 1. Athletes */}
          <Section n={1} title="Select athletes">
            <div className="flex flex-col divide-y divide-bm-mutedbg">
              {[athlete, ...otherUncontacted.filter((a) => extraIds.includes(a.athleteId))].map((a) => (
                <div key={a.athleteId} className="flex items-center justify-between py-3">
                  <span className="flex flex-col gap-[2px]">
                    <span className="bm-label-l text-bm-ink">{fullName(a)}</span>
                    <span className="bm-body-s text-bm-muted">
                      {[a.position, a.school, a.cacheScore ? `CacheScore ${a.cacheScore}` : null].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  {a.athleteId !== athleteId && (
                    <button type="button" onClick={() => setExtraIds((ids) => ids.filter((x) => x !== a.athleteId))} className="bm-label-xs text-bm-tertiary hover:text-bm-critical">
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="border-t border-bm-mutedbg pt-3">
              <button type="button" onClick={() => setShowOthers((s) => !s)} className="bm-label-l text-bm-orange">
                + Add from this campaign{" "}
                <span className="bm-body-m text-bm-muted">· send the same terms to other shortlisted athletes</span>
              </button>
              {showOthers && (
                <div className="mt-3 flex flex-col gap-2">
                  {otherUncontacted.filter((a) => !extraIds.includes(a.athleteId)).length === 0 && (
                    <span className="bm-body-s text-bm-muted">
                      Everyone else on this campaign has been contacted.{" "}
                      <button type="button" onClick={() => navigate("/brand/discover")} className="text-bm-orange underline">Browse Discover</button>
                    </span>
                  )}
                  {otherUncontacted
                    .filter((a) => !extraIds.includes(a.athleteId))
                    .map((a) => (
                      <Checkbox
                        key={a.athleteId}
                        checked={false}
                        onChange={() => setExtraIds((ids) => [...ids, a.athleteId])}
                        label={`${fullName(a)} · ${[a.position, a.school].filter(Boolean).join(" · ")}`}
                      />
                    ))}
                </div>
              )}
            </div>
          </Section>

          {/* 2. Offer details */}
          <Section
            n={2}
            title="Offer details"
            right={
              <button type="button" onClick={() => setEditingRows((e) => !e)} className="bm-label-xs text-bm-orange">
                {editingRows ? "DONE" : "DELIVERABLES"}
              </button>
            }
          >
            {editingRows ? (
              <div className="flex flex-col gap-2">
                {rows.map((row, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2 md:flex-nowrap">
                    <Select value={row.platform} onChange={(e) => setRow(i, { platform: e.target.value })} className="!h-9 md:!w-[160px]">
                      {PLATFORM_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </Select>
                    <Select value={row.contentType} onChange={(e) => setRow(i, { contentType: e.target.value })} className="!h-9 md:!w-[160px]">
                      {CONTENT_TYPE_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </Select>
                    <Input type="number" min={1} value={row.quantity} onChange={(e) => setRow(i, { quantity: Math.max(1, Number(e.target.value) || 1) })} className="!h-9 md:!w-[90px]" />
                    <Input value={row.notes || ""} onChange={(e) => setRow(i, { notes: e.target.value })} placeholder="Notes" className="!h-9 flex-1" />
                    <button type="button" onClick={() => setRows((r) => r.filter((_, idx) => idx !== i))} className="bm-body-m text-bm-tertiary hover:text-bm-critical">×</button>
                  </div>
                ))}
                <button type="button" onClick={() => setRows((r) => [...r, { platform: "Instagram", contentType: "Post", quantity: 1, notes: "" }])} className="self-start bm-label-xs text-bm-orange">
                  + Add deliverable
                </button>
              </div>
            ) : (
              <GrayBox>
                {lines.length ? lines.map((l) => <p key={l}>• {l}</p>) : <p className="text-bm-muted">No deliverables yet. Click DELIVERABLES to add some.</p>}
              </GrayBox>
            )}
            {estimate && (
              <p className="bm-body-s text-bm-muted">
                Rate-card estimate for this mix: {fmtRange(estimate, { compact: false })}
              </p>
            )}
          </Section>

          {/* 3. Compensation */}
          <Section n={3} title="Compensation · Fee transparency">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-[6px] rounded-[8px] bg-bm-subtle p-4">
                <Overline>Athlete compensation</Overline>
                <span className="flex items-baseline gap-[2px] bm-num-xl text-bm-orange">
                  $
                  <input
                    inputMode="numeric"
                    value={compensation}
                    onChange={(e) => setCompensation(e.target.value.replace(/[^\d,]/g, ""))}
                    onBlur={() => setCompensation(comp ? comp.toLocaleString() : "")}
                    placeholder="1,000"
                    className="bm-bare w-40 bg-transparent bm-num-xl text-bm-orange outline-none placeholder:text-bm-placeholder"
                  />
                </span>
                <span className="bm-body-s text-bm-muted">The athlete earns the full amount. You pay all Cache and transfer fees.</span>
              </div>
              <div className="flex flex-col gap-[6px] rounded-[8px] border border-bm-border bg-bm-subtle p-4">
                <Overline>Brand total cost</Overline>
                <span className="bm-num-xl text-bm-ink">{fmtMoney(cost.total)}</span>
                <span className="bm-body-s text-bm-muted">Includes {fmtMoney(cost.fee)} Cache fee. Fees are charged to the brand.</span>
              </div>
            </div>
          </Section>

          {/* 4. Milestones */}
          <Section n={4} title="Payment schedule · Milestones">
            <div className="flex flex-col gap-3">
              {milestones.map((m, i) => (
                <div key={m.id || i} className="flex flex-wrap items-center gap-3 border-b border-bm-mutedbg pb-3 last:border-b-0">
                  <span className="h-2 w-2 rounded-full bg-bm-orange" />
                  <span className="flex items-center gap-1 bm-label-l text-bm-ink">
                    $
                    <input
                      inputMode="numeric"
                      value={m.amount}
                      onChange={(e) =>
                        setMilestones((ms) => ms.map((x, idx) => (idx === i ? { ...x, amount: Number(e.target.value.replace(/[^\d]/g, "")) || 0 } : x)))
                      }
                      onBlur={() => setMilestones((ms) => rebalanceMilestones(ms, comp))}
                      className="bm-bare w-20 bm-label-l text-bm-ink"
                    />
                  </span>
                  <span className="bm-body-m text-bm-muted">·</span>
                  <input
                    value={m.label}
                    onChange={(e) => setMilestones((ms) => ms.map((x, idx) => (idx === i ? { ...x, label: e.target.value } : x)))}
                    className="bm-bare min-w-[200px] flex-1 bm-body-m text-bm-muted"
                  />
                </div>
              ))}
              {!milestones.length && <span className="bm-body-m text-bm-muted">Enter compensation to build the schedule.</span>}
            </div>
          </Section>

          {/* 5. Duration */}
          <Section n={5} title="Campaign duration">
            <h3 className="bm-h-m text-bm-ink">{fmtLongRange(startDate, endDate) || "Set the campaign dates"}</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <Toggle on={exclusivityEnabled} onChange={setExclusivityEnabled} label="Exclusivity" />
                <span className="bm-body-m text-bm-muted">
                  Athlete cannot post competing brands for
                  <input
                    type="number"
                    min={0}
                    max={365}
                    value={exclusivityDays}
                    disabled={!exclusivityEnabled}
                    onChange={(e) => setExclusivityDays(Number(e.target.value) || 0)}
                    className="bm-bare mx-1 !w-14 !rounded-[4px] !border !border-bm-border !px-1 text-center bm-label-l text-bm-ink disabled:text-bm-tertiary"
                  />
                  days after each post
                </span>
              </div>
              <Checkbox checked={requireProof} onChange={setRequireProof} label="Require proof of content (screenshot + link) before releasing each milestone payment" />
            </div>
          </Section>

          {/* 6. Scope of work */}
          <Section n={6} title="Scope of work" right={<span className="bm-label-xs text-bm-orange">DELIVERABLES</span>}>
            <TextArea value={scopeOfWork} onChange={(e) => setScopeOfWork(e.target.value)} className="!min-h-[240px] !bg-bm-subtle !border-bm-strong" />
          </Section>

          {/* 7. Message */}
          <Section n={7} title="Personalized message">
            <TextArea value={message} onChange={(e) => setMessage(e.target.value)} className="!min-h-[180px] !bg-bm-subtle !border-bm-strong" />
          </Section>
        </div>

        <div className="w-full lg:w-[340px]">
          <Card className="flex flex-col gap-4">
            <Overline>Cache offer system</Overline>
            <span className="bm-h-xs text-bm-ink">Safe &amp; Protected Deals</span>
            <p className="bm-body-s text-bm-muted">
              Offers remain open for {offerOpenDays || 7} days. Once accepted, both parties sign the contract. Funds are
              securely locked in Cache Escrow and automatically released as deliverables are approved.
            </p>
            {brand?.company && (
              <p className="bm-body-s text-bm-muted">Sent as {brand.company}.</p>
            )}
          </Card>
        </div>
      </div>

      <ActionBar
        left={
          <>
            <Btn onClick={() => submit("send")} disabled={!!saving || !rows.length}>
              {saving === "send" ? "Sending..." : targets > 1 ? `Send offer to ${targets} athletes` : alreadySent ? "Resend offer" : "Send offer"}
            </Btn>
            <Btn variant="secondary" onClick={() => submit("draft")} disabled={!!saving}>
              {saving === "draft" ? "Saving..." : "Save draft"}
            </Btn>
          </>
        }
        right={
          <button type="button" onClick={() => navigate(`/brand/campaigns/${campaignId}`)} className="bm-label-l text-bm-muted hover:text-bm-ink">
            Cancel
          </button>
        }
      />
    </div>
  );
};

export default ComposeOfferPage;
